import React, { useState, useEffect } from 'react';
import { X, Bell, Clock, User, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationsModal = ({ onClose, onRefresh }) => {
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  useEffect(() => {
    fetchUpcoming();
  }, [days]);

  const fetchUpcoming = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/emr/appointments/upcoming?days=${days}`,
        { withCredentials: true }
      );
      setUpcoming(response.data.upcoming || []);
    } catch (error) {
      console.error('Failed to fetch upcoming:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysColor = (daysUntil) => {
    if (daysUntil === 0) return 'text-red-400';
    if (daysUntil === 1) return 'text-orange-400';
    return 'text-yellow-400';
  };

  const getDaysText = (daysUntil) => {
    if (daysUntil === 0) return 'TODAY';
    if (daysUntil === 1) return 'TOMORROW';
    return `IN ${daysUntil} DAYS`;
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden border border-yellow-500/30">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-400/10 to-yellow-600/10 border-b border-yellow-500/30 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Bell className="w-8 h-8 text-yellow-400" />
              <div>
                <h2 className="text-2xl font-bold text-yellow-400">Upcoming Appointments</h2>
                <p className="text-gray-300 text-sm">Reminders and notifications</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="p-4 border-b border-gray-800 bg-black/20">
          <div className="flex items-center space-x-4">
            <span className="text-gray-300 text-sm">Show appointments within:</span>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-yellow-400"
            >
              <option value={1}>1 day</option>
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-gray-400">Loading...</div>
            </div>
          ) : upcoming.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No upcoming appointments</p>
              <p className="text-gray-500 text-sm mt-2">All clear for the next {days} days</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcoming.map((apt, index) => (
                <div
                  key={apt.id || index}
                  className="bg-black/30 border border-gray-800 rounded-xl p-4 hover:border-yellow-500/30 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-yellow-400/20 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-yellow-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold">{apt.patient_name}</h3>
                        <div className="flex items-center space-x-3 mt-1">
                          <div className="flex items-center space-x-1 text-gray-400 text-sm">
                            <Phone className="w-3 h-3" />
                            <span>{apt.patient_phone}</span>
                          </div>
                          <div className="flex items-center space-x-1 text-gray-400 text-sm">
                            <Clock className="w-3 h-3" />
                            <span>{apt.appointment_time}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold text-sm ${getDaysColor(apt.days_until)}`}>
                        {getDaysText(apt.days_until)}
                      </div>
                      <div className="text-gray-400 text-xs mt-1">{apt.appointment_date}</div>
                    </div>
                  </div>
                  <div className="mt-3 bg-yellow-400/10 border border-yellow-500/30 rounded-lg p-2">
                    <p className="text-gray-300 text-sm font-medium">{apt.service}</p>
                  </div>
                  <span className={`mt-2 inline-block px-2 py-1 rounded-full text-xs ${
                    apt.source === 'booking'
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-purple-500/20 text-purple-400'
                  }`}>
                    {apt.source === 'booking' ? '🌐 Online' : '👤 Manual'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-800 p-4 bg-gray-900/50">
          <div className="flex justify-between items-center">
            <div className="text-gray-400 text-sm">
              {upcoming.length} appointment{upcoming.length !== 1 ? 's' : ''} in next {days} days
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 text-black font-semibold rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsModal;
